/**
 * Words for signals in the UI (I8): a condition as a sentence, a value with its note and age. Built-in signal
 * names and notes have i18n keys (`signals.names.*`, `signals.notes.*`); others show their label or name.
 */
import type { SignalAst, SignalValue } from "../core/signals";

type T = (key: string, opts?: Record<string, unknown>) => string;
export interface SignalDef {
  type?: "number" | "boolean" | "text";
  label?: string;
  unit?: string;
  ttlMin?: number;
  source?: string;
  enabled?: boolean;
}

export function signalLabel(name: string, def: SignalDef | undefined, t: T): string {
  return t(`signals.names.${name.replace(/\./g, "_")}`, { defaultValue: def?.label || name });
}

export function valueText(v: unknown, t: T, unit?: string): string {
  if (v === true) return t("signals.yes");
  if (v === false) return t("signals.no");
  if (v === "" || v === null || v === undefined) return t("signals.none");
  return unit ? `${String(v)} ${unit}` : String(v);
}

export function noteText(name: string, rec: SignalValue | undefined, t: T): string | null {
  if (!rec?.note) return null;
  const key = name.replace(/\./g, "_");
  // a note can depend on the value ("sunset 19:12" while the sun is up, "sunrise 07:03" while it is down)
  const byValue = t(`signals.notes.${key}_${String(rec.value)}`, { note: rec.note, defaultValue: "" });
  return byValue || t(`signals.notes.${key}`, { note: rec.note, defaultValue: rec.note });
}

export function ageText(rec: SignalValue | undefined, nowMs: number, t: T): string | null {
  if (!rec?.at) return null;
  const min = Math.max(0, Math.round((nowMs - Date.parse(rec.at)) / 60_000));
  if (min < 1) return t("signals.ageNow");
  if (min < 120) return t("signals.ageMin", { n: min });
  return t("signals.ageHours", { n: Math.round(min / 60) });
}

const OPS = ["eq", "neq", "gt", "lt", "gte", "lte", "in", "has"] as const;

export function describeCondition(ast: SignalAst, defs: Record<string, SignalDef>, t: T): string {
  if (Array.isArray(ast.and))
    return (ast.and as SignalAst[]).map((a) => describeCondition(a, defs, t)).join(t("signals.and"));
  if (Array.isArray(ast.or))
    return (ast.or as SignalAst[]).map((a) => describeCondition(a, defs, t)).join(t("signals.or"));
  if (ast.not && typeof ast.not === "object")
    return t("signals.not", { what: describeCondition(ast.not as SignalAst, defs, t) });
  if (typeof ast.signal === "string") {
    const name = ast.signal;
    const def = defs[name];
    const op = OPS.find((o) => o in ast) ?? "truthy";
    const raw = op === "truthy" ? undefined : ast[op];
    const value = Array.isArray(raw)
      ? raw.map((x) => valueText(x, t)).join(", ")
      : valueText(raw, t, def?.unit);
    return t(`signals.op.${op}`, { signal: signalLabel(name, def, t), value });
  }
  if (typeof ast.prop === "string" && "has" in ast)
    return t("signals.propHas", { prop: ast.prop, value: String(ast.has) });
  return JSON.stringify(ast);
}
